// @flow

import moment from 'moment';
import _ from 'lodash';
import keyMirror from 'keymirror';
import I18n from '../../i18n/I18n';

import deprecated from '../../decorators/deprecated';
import ArrayUtils from '../../utils/ArrayUtils';
import DateUtils from '../../utils/DateUtils';

import ApiObject from '../ApiObject';
import ApiConstants from '../../constants/ApiConstants';

import Logger from '../../utils/Logger';

import Check from '../Check';
import User from '../user/User';

import OrderProduct from './OrderProduct';
import OrderMenu from './OrderMenu';
import UserAddress from '../user/UserAddress';
import OrderSettings from './OrderSettings';
import OrderSource from './OrderSource';
import UserAccountPayment from './UserAccountPayment';

import RestaurantMenu from '../menu/RestaurantMenu';

export default class Order extends ApiObject {

  static Priority =  keyMirror({
    URGENT: null,
    WARNING: null,
    WAITING: null,
    LATER: null
  });

  static OrderMode = {
    ON_SITE: 0,

    TAKE_AWAY: 1,

    DELIVERY: 2
  };


  static PaymentMode = {
    CREDIT_CARD: 0,

    ON_SITE: 1,

    USER_ACCOUNT: 2,
  };

  static ON_SITE_PAYMENT_ID: number = 0;

  static OrderStatus = ApiConstants.OrderStatus;

  id: string = '';

  mode: OrderMode = Order.OrderMode.ON_SITE;

  products: Array<OrderProduct> = [];

  menus: Array<OrderMenu> = [];

  comment: string;

  /**
   * See Order.OrderStatus
   * @type {string}
   */
  status: string = Order.OrderStatus.CREATED;

  userId: string = '';

  restaurantId: string = '';

  checkId: string = '';

  paymentId: string = '';

  /**
   * distinct weither the user pays at the counter or with credit card
   * front only for now
   * @type {int}
   */
  paymentMode: int;
  /**
   * moment instance
   * @type {moment}
   */
  dueDate: ?Object = null;

  modifiers: Array = [];

  /**
   * moment instance
   * @type {moment}
   */
  creationDate: ?Object = null;

  /**
   * moment instance
   * @type {moment}
   */
  modificationDate: ?Object = null;

  /*
   * front only
   */
  check: ?Check = null;

  /*
   * front only
   */
  user: ?User = null;

  /**
   * In case of DELIVERY, contains the user's address where the delivery have to be made.
   */
  address: ?UserAddress = null;

  /**
   *
   */
  userAccountPayment: ?UserAccountPayment = null;

  /**
   * an id generated by the api consumer to avoid the order/payment to be sent multiple times to the API. If the order request fails, the next order request should be sent with the same clientKey value. This value could be the timestamp of when the api consumer created the Order object on its side
   */
  clientKey: string;

  tableNumber: ?string = '';

  /**
   * In case of DELIVERY mode, contains the delivery price to add.
   * Note: In the plugin, this var must be 0 until we sent the order.
   */
  /**
   * delivery time, set by the front.
   */
  deliveryTime: number = 0;

  /**
   * List of all items orderId (order products and menu) in the cart (sorted by add)
   * @type {Array}
   */
  listItemsCart: [string] = [];

  //
  //
  //

  postJsonAssignation(data: Object) {
    if (!_.isNil(data.id)) {
      this.id = data.id.toString();
    }

    this.dueDate = moment(data.dueDate);
    this.creationDate = moment(data.creationDate);
    this.modificationDate = moment(data.modificationDate);

    if (!_.isNil(data.userAccountPayment)) {
      this.userAccountPayment = new UserAccountPayment().fromJson(data.userAccountPayment);
    }

    this.products = [];
    _.each(data.products, (productData) => {
      const product:OrderProduct = new OrderProduct();
      product.fromJson(productData);
      this.products.push(product);
    });


    this.menus = [];
    _.each(data.menus, (menuData) => {
      const menu:OrderMenu = new OrderMenu();
      menu.fromJson(menuData);
      this.menus.push(menu);
    });

    this.source = new OrderSource().fromJson(data.source);
  }

  toJSON() {
    const json = super.toJSON();

    if (!_.isNull(this.dueDate)) {
      json.dueDate = this.dueDate.valueOf();
    }
    delete json['creationDate'];
    delete json['modificationDate'];

    // split products per quantity
    const productsDetached = [];
    _.each(this.products, (product) => {
      for (let i = 0; i < product.quantity; i++) {
        let splittedProduct = product.copy();

        // to handle preview and reconstruct listItemsCart, we don't want to change the orderId
        // that is currently saved on listItemsCart.
        if (i !== 0) {
          splittedProduct.populateOrderId();
        }
        productsDetached.push(splittedProduct);
      }
    });

    json['products'] = productsDetached;

    // split menu per quantity
    const menusDetached = [];
    _.each(this.menus, (menu) => {
      for (let i = 0; i < menu.quantity; i++) {
        let splittedMenu = menu.copy();

        // to handle preview and reconstruct listItemsCart, we don't want to change the orderId
        // that is currently saved on listItemsCart.
        if (i !== 0) {
          splittedMenu.populateOrderId();
        }
        menusDetached.push(splittedMenu);
      }
    });

    json['menus'] = menusDetached;

    return json;
  }

  /**
   * get the current products and menus, and populate the listItemsCart with it.
   * Used for order preview.
   * We regroup products / menus that are equals.
   * We sort the listItemsCart in the same order than the ids given in parameter.
   * @param  {[type]} orderBy an array of orderId, to order our listItemsCart in the same order
   */
  populateListItemsCart(orderByIds: Array<string>) {
    // contains all the current order ids.
    let allOrderIds = [];

    // contains the new listItemsCart
    let listItemsCart = [];

    // contains all products without double
    let productsUnique = [];
    this.products.forEach((product: OrderProduct) => {
      if (productsUnique.length > 0) {
        let loopFlag = true,
            pushFlag = false,
            pushProduct;

        productsUnique.forEach((baseProduct: OrderProduct, index) => {
          if (loopFlag) {
            if (baseProduct.equals(product)) {
              loopFlag = false
              pushFlag = false;
              productsUnique[index].quantity++;
            } else {
              pushFlag = true;
              pushProduct = product;
            }
          }
        });

        if (pushFlag) {
          productsUnique.push(pushProduct);
        }
      } else {
        productsUnique.push(product);
      }
    });

    this.products = productsUnique;

    // contains all products without double
    let menusUnique = [];
    this.menus.forEach((menu: OrderMenu) => {
      if (menusUnique.length > 0) {
        let pushFlag = false;
        let pushMenu;
        menusUnique.forEach((baseMenu: OrderMenu, index) => {
          if (baseMenu.equals(menu)) {
            pushFlag = false;
            menusUnique[index].quantity++;
          } else {
            pushFlag = true;
            pushMenu = menu;
          }
        });

        if (pushFlag) {
          menusUnique.push(pushMenu);
        }
      } else {
        menusUnique.push(menu);
      }
    });

    this.menus = menusUnique;

    // get all the current order ids (products and menus are degrouped)
    allOrderIds = _.map(_.concat(this.products, this.menus), (item: OrderMenuOrProduct) => {
      return item.orderId;
    });

    // begin to put the old order ids on our new listItemsCart.
    listItemsCart = _.filter(orderByIds, (orderId: string) => {
      // if we can't find the item, then the API removed id, or an error occured somewhere.
      // so we don't add the id.
      return !_.isNull(this.getItemWithOrderId(orderId));
    });

    // contains all ids that are not on the param orderByIds.
    // all the new ids -> degroupate products / menus, products / menus that has been changed.
    const diff = _.difference(allOrderIds, listItemsCart);

    // our new products / menus array.
    const products = [];
    const menus = [];

    // populate our products / menus array with our items that we already have.
    _.each(listItemsCart, (orderId: string) => {
      const item = this.getItemWithOrderId(orderId);
      // item could not be null since `listItemsCart` is filtered and remove null objects.
      if (item instanceof OrderMenu) {
        menus.push(item.copy());
      } else {
        products.push(item.copy());
      }
    });

    // for all non matching order id with the given one in param, we search for an equal item
    // and merge it -> REGROUP menus / products
    _.each(diff, (orderId: string) => {
      const item = this.getItemWithOrderId(orderId);
      if (!_.isNil(item)) {
        if (item instanceof OrderMenu) {
          // find a menu that is equal
          const menu: ?OrderMenu = this.findEqualsMenu(item);

          if (_.isNull(menu)) { // it is not a regroup, we add it.
            listItemsCart.push(orderId);
            menus.push(item.copy());
          } else { // we regroup.
            // obj is the menu that we already add on our new products array, if exists.
            const obj = ArrayUtils.find(menus, (m) => { return m.orderId === menu.orderId })

            if (!_.isNull(obj)) { // already exists on our new array, update quantity
              obj.quantity++;
            } else { // do not exists, add it.
              const menuToUse = menu.copy();
              listItemsCart.push(menuToUse.orderId);
              menus.push(menuToUse);
            }
          }
        } else {
          // find a product that is equal
          const product: ?OrderProduct = this.findEqualsProduct(item);

          if (_.isNull(product)) { // it is not a regroup, we add it.
            listItemsCart.push(orderId);
            products.push(item.copy());
          } else { // we regroup
            // obj is the product that we already add on our new products array, if exists.
            const obj = ArrayUtils.find(products, (p) => { return p.orderId === product.orderId })

            if (!_.isNull(obj)) { // already exists on our new array, update quantity
              obj.quantity++;
            } else { // do not exists, add it.
              const productToUse = product.copy();
              listItemsCart.push(productToUse.orderId);
              products.push(productToUse);
            }
          }
        }
      }
    });

    // in case there is a problem on our algorithm.
    if (products.length + menus.length !== listItemsCart.length) {
      Logger.error('Order.populateListItemsCart', 'error in algorithm');
    }

    // sort
    this.listItemsCart = listItemsCart;
    this.products = products;
    this.menus = menus;
  }

  mergeUiDatas(previousOrder) {
    this.products.forEach((product: OrderProduct) => {
      const previousProduct = previousOrder.getProductWithId(product.id)
      if (!_.isEmpty(previousProduct)) {
        product.ui = previousProduct.ui
      }
    })

    this.menus.forEach((menu: OrderMenu) => {
      const previousMenu = previousOrder.getMenuWithId(menu.id)
      if (!_.isEmpty(previousMenu)) {
        menu.ui = previousMenu.ui
      }
    })
  }

  @deprecated
  getFormattedPickupDate(): string {
    return this.getFormattedDueDate();
  }

  /**
   * Returns the pickup date as a complete string.
   * @returns {String}
   */
  getFormattedDueDate(): string {
    let begin = '';
    const today = moment();
    const tomorrow = moment().add(1, 'days');

    if (DateUtils.isSameDay(this.dueDate, tomorrow)) {
      begin = I18n.tr('order.tomorrow_at');
    } else if (DateUtils.isSameDay(this.dueDate, today)) {
      begin = ''; //'Aujourd\'hui à';
    } else {
      begin = `${this.dueDate.format('DD/MM')} ${I18n.tr('order.at')}`;
    }

    return `${begin} ${this.dueDate.format('HH:mm')}`;
  }

  getTableNumberString(): string {
    if (this.isOnBar()) {
      return I18n.tr('order.at_bar');
    }

    return this.tableNumber;
  }

  isOnBar(): boolean {
    return !_.isNull(this.tableNumber) && this.tableNumber.toLowerCase() === 'bar';
  }

  isDeliveryMode(): boolean {
    return this.mode === Order.OrderMode.DELIVERY;
  }

  isTakeAwayMode(): boolean {
    return this.mode === Order.OrderMode.TAKE_AWAY;
  }

  isOnSiteMode(): boolean {
    return this.mode === Order.OrderMode.ON_SITE;
  }

  isPayed(): boolean {
    if (this.userAccountPayment !== null) {
      return true
    }

    if (this.paymentId === Order.ON_SITE_PAYMENT_ID) {
      return false;
    }

    if (_.isNil(this.check)) {
      return false;
    }

    return this.check.outstandingBalance === 0;
  }

  isOnSitePayment(): boolean {
    return this.paymentId === Order.ON_SITE_PAYMENT_ID;
  }

  //
  // Status
  //

  isOnGoing(): boolean {
    return !this.isCanceled() && !this.isFailed()
      && !this.isDelivered() && !this.isCreated();
  }
  
  isForHistory() {
    return this.isCanceled() || this.isDelivered();
  }

  isCreated(): boolean {
    return this.status === Order.OrderStatus.CREATED;
  }

  isFailed(): boolean {
    return this.status === Order.OrderStatus.FAILED;
  }

  isCanceled(): boolean {
    return this.status === Order.OrderStatus.CANCELED;
  }

  isDelivered(): boolean {
    return this.status === Order.OrderStatus.DELIVERED;
  }

  isReceived(): boolean {
    return this.status === Order.OrderStatus.RECEIVED;
  }

  isSent(): boolean {
    return this.status === Order.OrderStatus.SENT;
  }


  /**
   * Returns the last 3 digits of the order number.
   * If the check is null or does not contains any pos check number, we use the order id.
   */
  getOrderNumber(): string {
    if (_.isNull(this.check) || _.isEmpty(this.check.posCheckNumber)) {
      return this.id.substr(this.id.length - 3);
    }

    return this.check.posCheckNumber.substr(this.check.posCheckNumber.length - 3);
  }

  /**
   * Returns the last 4 digits of the order number.
   * If the check is null or does not contains any pos check number, we use the order id.
   */
  getOrderNumberMedium(): string {
    if (_.isNull(this.check) || _.isEmpty(this.check.posCheckNumber)) {
      return this.id.substr(this.id - 4);
    }

    return this.check.posCheckNumber.substr(this.check.posCheckNumber.length - 4);
  }

  /**
   * Returns the entire order number.
   * If the check is null or does not contains any pos check number, we use the order id.
   */
  getFullOrderNumber(): string {
    if (_.isNull(this.check) || _.isEmpty(this.check.posCheckNumber)) {
      return this.id;
    }

    return this.check.posCheckNumber;
  }

  //
  //
  //

  haveReachMinimumPriceWithOrderSettings(orderSettings: OrderSettings): boolean {
    const minimumPrice: Price = orderSettings.getForMode(this.mode).minimumPrice;

    return minimumPrice === 0 || this.getTotalPrice() >= minimumPrice;
  }

  //
  //
  //

  countItems() {
    let products = { quantity: 0 },
          menus = { quantity: 0 };
    if (this.products.length > 0) {
      products = this.products.reduce((a, b) => ({
        quantity: a.quantity + b.quantity
      }))
    }

    if (this.menus.length > 0) {
      menus = this.menus.reduce((a, b) => ({
        quantity: a.quantity + b.quantity
      }))
    }

    return products.quantity + menus.quantity
  }

  /**
   * Warning: do not use for DELIVERY mode when creating the order since the fees
   * depends of the orderSettings. 
   */
  getTotalPrice(): number {
    let total = 0;

    _.each(this.menus, (orderMenu: OrderMenu) => {
      total += orderMenu.getTotalPriceWithQuantity();
    });

    _.each(this.products, (orderProduct: OrderProduct) => {
      total += orderProduct.getTotalPriceWithQuantity();
    });

    _.each(this.fees, (fee) => {
      total += fee.amountIncludingTax;
    });
    
    /**
     * operations on double/float values results on total 10.100000000000001
     * so we round it
     */
    return Math.round(total * 100) / 100
  }

  getTotalPriceWhithoutTax(): number {
    if (_.isEmpty(this.check) || _.isEmpty(this.check.tax)) {
      return this.getTotalPrice()
    }

    let totalPriceWhithoutTax = 0
    _.forEach(this.check.tax, tax => {
      totalPriceWhithoutTax += parseFloat(tax.amoutExclTax)
    })

    return totalPriceWhithoutTax
  }

  /**
   * Returns the total price without the delivery fee.
   */
  getTotalCartPrice(): number {
    let total = 0;

    _.each(this.menus, (orderMenu: OrderMenu) => {
      total += orderMenu.getTotalPriceWithQuantity();
    });

    _.each(this.products, (orderProduct: OrderProduct) => {
      total += orderProduct.getTotalPriceWithQuantity();
    });

    return total;
  }

  //
  // used on BO for ticket print
  //
  @deprecated
  getMenusWithQuantities() {
    // contains the list of products, with the quantity for each
    return _.map(this.menus, (orderMenu: OrderMenu) => {
      return {
        quantity: orderMenu.quantity,
        orderMenu
      };
    });
  }

  //
  // used on BO for ticket print
  //
  @deprecated
  getProductsWithQuantities() {
    // contains the list of products, with the quantity for each
    return _.map(this.products, (orderProduct: OrderProduct) => {
      return {
        quantity: orderProduct.quantity,
        orderProduct
      };
    });
  }

  /**
   * Returns the cart content items, as an array of Product / Menus, sort by the added time.
   */
  getCartItems(): Array<OrderMenuOrProduct> {
    return _.map(this.listItemsCart, (itemOrderId: string) => {
      return this.getItemWithOrderId(itemOrderId);
    });
  }

  static getNextStatus(status, mode): string {
    switch (status) {
      case Order.OrderStatus.CREATED:
        return Order.OrderStatus.SENT

      case Order.OrderStatus.SENT:
        return Order.OrderStatus.RECEIVED;

      case Order.OrderStatus.RECEIVED:
        return Order.OrderStatus.PRODUCING;

      case Order.OrderStatus.PRODUCING:
        return Order.OrderStatus.READY;

      case Order.OrderStatus.READY:
        if (mode === Order.OrderMode.DELIVERY) {
          return Order.OrderStatus.IN_DELIVERY;
        } else {
          return Order.OrderStatus.DELIVERED;
        }

      case Order.OrderStatus.IN_DELIVERY:
        return Order.OrderStatus.DELIVERED;

      case Order.OrderStatus.DELIVERED:
        return '';

      case Order.OrderStatus.CANCELED:
        return '';

      default:
        Logger.warning('Order', `getNextStatus: invalid mode ${mode}`)
        return '';
    }
  }

  static getNextStatusForRestaurateur(status, mode): string {
    switch (status) {
      case Order.OrderStatus.CREATED:
        return Order.OrderStatus.RECEIVED;

      case Order.OrderStatus.SENT:
        return Order.OrderStatus.RECEIVED;

      case Order.OrderStatus.RECEIVED:
        return Order.OrderStatus.READY;

      // case Order.OrderStatus.PRODUCING:
      //   return Order.OrderStatus.READY;

      case Order.OrderStatus.READY:
        if (mode === Order.OrderMode.DELIVERY) {
          return Order.OrderStatus.IN_DELIVERY;
        }
        return Order.OrderStatus.DELIVERED;

      case Order.OrderStatus.IN_DELIVERY:
        return Order.OrderStatus.DELIVERED;

      case Order.OrderStatus.DELIVERED:
        return '';

      case Order.OrderStatus.CANCELED:
        return '';

      default:
        Logger.warning('Order', `getNextStatus: invalid mode ${mode}`);
        return '';
    }
  }

  static getStepsWithDelivery() {
    return [
      Order.OrderStatus.SENT,
      Order.OrderStatus.RECEIVED,
      // Order.OrderStatus.PRODUCING,
      Order.OrderStatus.READY,
      Order.OrderStatus.IN_DELIVERY,
      Order.OrderStatus.DELIVERED,
    ];
  }

  static getStepsWithoutDelivery() {
    return [
      Order.OrderStatus.SENT,
      Order.OrderStatus.RECEIVED,
      // Order.OrderStatus.PRODUCING,
      Order.OrderStatus.READY,
      Order.OrderStatus.DELIVERED,
    ];
  }

  static isStatusBefore(statusToCheck, status) {
    const indexStatus = _.findIndex(Order.getStepsWithDelivery(), (a) => {
      return status === a;
    });

    const indexStatusToCheck = _.findIndex(Order.getStepsWithDelivery(), (a) => {
      return statusToCheck === a;
    });

    return indexStatusToCheck < indexStatus;
  }

  getRemainingTime(): Moment {
    return moment.duration(this.dueDate.valueOf() - moment().valueOf(), 'ms');
  }

  getRemainingTimeLabel(): string {
    const remainingTime = this.getRemainingTime();


    if (remainingTime.hours() < 0 || remainingTime.minutes() < 0 || remainingTime.hours() > 2 || remainingTime.days() > 0) {
      return (this.getFormattedDueDate());
    }

    if (remainingTime.hours() === 0) {
      return (`${remainingTime.minutes()} min`);
    }

    if (remainingTime.minutes() === 0) {
      return (`${remainingTime.hours()}h`);
    }

    return (`${remainingTime.hours()}h ${remainingTime.minutes()}`);
  }

  /**
   * Give a priority based on the dueDate and the delay.
   * The delay can be:
   * - the manufactureDelay
   * - in delivery mode, the calculate delay set on the order. don't need delay param here.
   *
   * @return  URGENT | WARNING | LATER | WAITING
   */
  getPriority(delayParam: number = null): string {
    // create dates for calculs
    // const dateLimitToPrepare = moment().add(delay, 'ms');
    const now = moment();

    const delay = this.isDeliveryMode() && _.isNull(delayParam) ? this.deliveryTime : delayParam;

    // copy to not screw the real
    const dueDate = moment(this.dueDate);

    const dueDateMinusFive = moment(dueDate).subtract(5, 'minutes');

    if (this.isDeliveryMode()) {
      const beginPreparationDate = DateUtils.fromMsTimestamp(dueDate.valueOf() - delay);
      if (dueDate.isBefore(now)) { // same as other modes
        return Order.Priority.URGENT;
      } else if (now.isAfter(dueDateMinusFive)) { // same as other modes
        // 5 minutes before the due date
        return Order.Priority.WARNING;
      } else if (beginPreparationDate.isBefore(now)) {
        return Order.Priority.WAITING;
      }

      return Order.Priority.LATER;
    }

    // due Date have been reached
    if (dueDate.isBefore(now)) {
      return Order.Priority.URGENT;
    } else if (now.isAfter(dueDateMinusFive)) {
      // 5 minutes before the due date
      return Order.Priority.WARNING;
    } else if (dueDate.valueOf() > now.valueOf() + delay) {
      // far in the future.
      return Order.Priority.LATER;
    }

    return Order.Priority.WAITING;
  }

  //
  //
  //

  getProductWithId(id) {
    return ArrayUtils.find(this.products, { id });
  }

  getMenuWithId(id) {
    return ArrayUtils.find(this.menus, { id });
  }

  getProductWithOrderId(orderId: string) {
    return ArrayUtils.find(this.products, (orderProduct: OrderProduct) => {
      return orderProduct.orderId === orderId;
    });
  }

  getMenuWithOrderId(orderId: string) {
    return ArrayUtils.find(this.menus, (orderMenu: OrderMenu) => {
      return orderMenu.orderId === orderId;
    });
  }

  getItemWithOrderId(orderId: string): OrderMenuOrProduct {
    const product = this.getProductWithOrderId(orderId);

    if (_.isNull(product)) {
      return this.getMenuWithOrderId(orderId);
    }

    return product;
  }

  addOnListItemsCart(orderId: string) {
    const exists = ArrayUtils.find(this.listItemsCart, (orderIdToCompare: string) => {
      return orderIdToCompare === orderId;
    });

    if (!exists) {
      this.listItemsCart.push(orderId);
    }
  }

  updateOnListItemsCart(oldOrderId: string, orderId: string) {
    this.listItemsCart = ArrayUtils.updateObject(this.listItemsCart, orderId, (orderIdToCompare) => {
      return orderIdToCompare === oldOrderId;
    });
  }

  removeOnListItemsCart(orderId: string) {
    this.listItemsCart = ArrayUtils.removeObject(this.listItemsCart, (orderIdToCompare: string) => {
      return orderIdToCompare === orderId;
    });
  }

  /**
   * Search for an orderProduct that is the same as the given one for the comment, options, etc.
   * @param  {[type]} orderProductToFind [description]
   * @return {[type]}                    [description]
   */
  findEqualsProduct(orderProductToFind: OrderProduct) {
    return ArrayUtils.find(this.products, (orderProduct: OrderProduct) => {
      return orderProductToFind.equals(orderProduct);
    });
  }

  addProduct(orderProduct: OrderProduct) {
    const existingOrderProduct = this.findEqualsProduct(orderProduct);

    if (_.isNull(existingOrderProduct)) { // new orderProduct
      if (_.isNil(orderProduct.orderId) || _.isEmpty(orderProduct.orderId)) {
        orderProduct.populateOrderId();
      }
      this.products.push(orderProduct);
      this.addOnListItemsCart(orderProduct.orderId);
    } else {
      const orderProductWithNewQuantity = existingOrderProduct.copy();
      orderProductWithNewQuantity.quantity += 1;
      this.updateProduct(existingOrderProduct, orderProductWithNewQuantity);
    }
  }

  updateProduct(oldOrderProduct: OrderProduct, orderProduct: OrderProduct) {
    this.products = ArrayUtils.updateObject(this.products, orderProduct, (orderProductToCompare) => {
      if (orderProductToCompare.orderId === oldOrderProduct.orderId) {
        this.updateOnListItemsCart(oldOrderProduct.orderId, orderProduct.orderId);
        return true;
      }
      return false;
    });
  }

  /**
   * Remove a product from the cart.
   * Note: If the product quantity is greater than 1, we reduce the quantity by 1 if the
   * removeAll parameter is set to false.
   * @param  {[type]} orderProduct [description]
   * @return {[type]}              [description]
   */
  removeProduct(orderProduct: OrderProduct, removeAll: boolean = false) {
    const existingOrderProduct = this.getProductWithOrderId(orderProduct.orderId);
    if (!_.isNull(existingOrderProduct)) {
      const orderProductWithNewQuantity = existingOrderProduct.copy();
      orderProductWithNewQuantity.quantity -= 1;

      if (orderProductWithNewQuantity.quantity >= 1 && removeAll === false) {
        this.updateProduct(existingOrderProduct, orderProductWithNewQuantity);
      } else {
        this.removeOnListItemsCart(orderProduct.orderId);
        this.products = ArrayUtils.removeObject(this.products, (orderProductToCompare: OrderMenu) => {
          return orderProduct.orderId === orderProductToCompare.orderId;
        });
      }
    }
  }

  removeProducts(orderProducts: Array<OrderProduct>) {
    _.each(orderProducts, (orderProduct: OrderProduct) => {
      this.removeProduct(orderProduct);
    });
  }

  /**
   * Search for an orderMenu that is the same as the given one for the comment, options, etc.
   * @param  {[type]} orderMenuToFind [description]
   * @return {[type]}                 [description]
   */
  findEqualsMenu(orderMenuToFind: OrderMenu) {
    return ArrayUtils.find(this.menus, (orderMenu: OrderMenu) => {
      return orderMenuToFind.equals(orderMenu);
    });
  }

  addMenu(orderMenu: OrderMenu) {
    const existingOrderMenu = this.findEqualsMenu(orderMenu);

    if (_.isNull(existingOrderMenu)) { // new orderMenu
      if (_.isNil(orderMenu.orderId) || _.isEmpty(orderMenu.orderId)) {
        orderMenu.populateOrderId();
      }
      this.menus.push(orderMenu);
      this.addOnListItemsCart(orderMenu.orderId);
    } else {
      const orderMenuWithNewQuantity = existingOrderMenu.copy();
      orderMenuWithNewQuantity.quantity += 1;
      this.updateMenu(existingOrderMenu, orderMenuWithNewQuantity);
    }
  }

  updateMenu(oldOrderMenu: OrderMenu, orderMenu: OrderMenu) {
    this.menus = ArrayUtils.updateObject(this.menus, orderMenu, (orderMenuToCompare) => {
      if (orderMenuToCompare.orderId === oldOrderMenu.orderId) {
        this.updateOnListItemsCart(oldOrderMenu.orderId, orderMenu.orderId);
        return true;
      }
      return false;
    });
  }

  /**
   * Remove a menu from the cart.
   * Note: If the menu quantity is greater than 1, we reduce the quantity by 1 if the
   * removeAll parameter is set to false.
   * @param  {[type]} orderMenu [description]
   * @return {[type]}              [description]
   */
  removeMenu(orderMenu: OrderMenu, removeAll: boolean = false) {
    const existingOrderMenu = this.getMenuWithOrderId(orderMenu.orderId);

    if (!_.isNull(existingOrderMenu)) {
      const orderMenuWithNewQuantity = existingOrderMenu.copy();
      orderMenuWithNewQuantity.quantity -= 1;

      if (orderMenuWithNewQuantity.quantity >= 1 && removeAll === false) {
        this.updateMenu(existingOrderMenu, orderMenuWithNewQuantity);
      } else {
        this.removeOnListItemsCart(orderMenu.orderId);
        this.menus = ArrayUtils.removeObject(this.menus, (orderMenuToCompare: OrderMenu) => {
          return orderMenu.orderId === orderMenuToCompare.orderId;
        });
      }
    }
  }

  removeMenus(orderMenus: Array<OrderMenu>) {
    _.each(orderMenus, (orderMenu: OrderMenu) => {
      this.removeMenu(orderMenu);
    });
  }

  isEmpty(): boolean {
    return _.isEmpty(this.products) && _.isEmpty(this.menus);
  }

  // check if max cancelable date is in the future
  isCancelable(): boolean {
    if (this.isCanceled() || this.isFailed()) {
      return false
    }
    if (_.isNil(this.limitRefundDate)) {
      return true
    }
    return Date.now() < this.limitRefundDate
  }

  //
  //
  //

  verifyItemsForDueDate(dueDateParam: Moment, restaurantMenu: RestaurantMenu): Array {
    const items = {
      products: [], // OrderProduct
      menus: [], // OrderMenu
    };

    const dueDate = moment(dueDateParam);

    _.each(this.products, (orderProduct: OrderProduct) => {
      const product: Product = restaurantMenu.getProductWithId(orderProduct.id);

      if (!product.isAvailableForDate(dueDate)) {
        items.products.push(orderProduct);
      }
    });

    _.each(this.menus, (orderMenu: OrderMenu) => {
      const menu: Menu = restaurantMenu.getMenuWithId(orderMenu.id);

      if (!menu.isAvailableForDate(dueDate)) {
        items.menus.push(orderMenu);
      }
    });

    return items;
  }

  /**
   * Genere the deliveryTime
   * @param orderSettings
   * @param deliveryDistanceTime in case the DeliveryTimeSettings.settingsType === DeliveryTimeSettings.Type.ADAPTABLE, the front have to calculate the distance time between the
   * delivery address and the restaurant address.
   */
  generateDeliveryTime(orderSettings, deliveryDistanceTime: MsTimestamp = null) {
    this.deliveryTime = orderSettings.getDeliveryTime(deliveryDistanceTime);
  }

}
